/**
 * This class:
 * - subscribes to ble characteristic notifications
 * - stores the data as a time series in this.data
 * - throttles so as to update state periodically
 */

import './BleChannel.scss';
import ChannelsService from 'data/ChannelsService';
import DataPoint from 'data/DataPoint.js';


class BleChannel {
  constructor(bluetoothDevice, characteristicUuid, throttleMillis) {
    this.data = [];
    this.active = true;
    this.statusStr = 'Retrieving...';
    this.bluetoothDevice = bluetoothDevice;
    this.channelsService = new ChannelsService.Factory().create();
    this.characteristicUuid = characteristicUuid;
    this.throttleMillis = throttleMillis;
    this.lastUpdateMillis = 0;
    this.subscribe();
    this._sync = false;
  }

  subscribe() {
    this.bluetoothDevice.gatt.connect()
        .then(server => { return server.getPrimaryServices(); })
        .then(svcs => { return svcs[0].getCharacteristics(); })
        .then(chs => {
            const ch = chs.filter(
                ch => ch.uuid.startsWith(this.characteristicUuid))[0];
            return ch.startNotifications();
        })
        .then(ch => {
          ch.addEventListener(
              'characteristicvaluechanged',
              this.onCharacteristicValueChanged.bind(this));
        })
        .catch(error => {
          console.log(error);
          console.log(error.message);
        });
  }

  onCharacteristicValueChanged(ev) {
    if (!this.active) {
      return;
    }
    const ch = ev.srcElement;
    const hr = parseHeartRate(ch.value).heartRate;
    const now = new Date();
    const nowMillis = now.getTime();
    this.data.push(DataPoint.of(now, hr).toTYFormat());
    if (nowMillis > this.lastUpdateMillis + this.throttleMillis) {
      this.lastUpdateMillis = nowMillis;
      this.channelsService.update(this.getId(), nowMillis);
    }
    this.statusStr = hr; 
  }

  getStatus() {
    return this.statusStr;
  }

  getId() {
    return this.bluetoothDevice.name;
  }

  getUnits() {
    return "bpm";  // For heart rate only.
  }

  getData() {
    return this.data;
  }

  clearData() {
    this.data = [];
  }

  setActive(active) {
    this.active = active;
    this.statusStr = active ? 'Resuming...' : 'Paused';
  }

  disconnect() {
    const gatt = this.bluetoothDevice.gatt;
    if (gatt.connected) {
      gatt.disconnect();
    }
  }

  get sync() {
    return this._sync;
  }

  set sync(b) {
    this._sync = b;
  }
}

const parseHeartRate = data => {
  let flags = data.getUint8(0);
  let rate16Bits = flags & 0x1;
  let result = {};
  let index = 1;
  if (rate16Bits) {
    result.heartRate = data.getUint16(index, /*littleEndian=*/true);
    index += 2;
  } else {
    result.heartRate = data.getUint8(index);
    index += 1;
  }
  let contactDetected = flags & 0x2;
  let contactSensorPresent = flags & 0x4;
  if (contactSensorPresent) {
    result.contactDetected = !!contactDetected;
  }
  let energyPresent = flags & 0x8;
  if (energyPresent) {
    result.energyExpended = data.getUint16(index, /*littleEndian=*/true);
    index += 2;
  }
  let rrIntervalPresent = flags & 0x10;
  if (rrIntervalPresent) {
    let rrIntervals = [];
    for (; index + 1 < data.byteLength; index += 2) {
      rrIntervals.push(data.getUint16(index, /*littleEndian=*/true));
    }
    result.rrIntervals = rrIntervals;
  }
  return result;
}

export default BleChannel;

